import { PropertyFormField } from '@components/Card2/CardFormFields/PropertyFormField';
import { canDeleteCardItem } from '@components/Card2/utils';
import WithPlaceholder from '@components/WithPlaceholder/WithPlaceholder';
import { cardEditContext } from '@context/card-edit-context';
import type { CardPropertyDetailsFragment } from '@graphql/fragments/cards/property.graphql';
import { useCardPropertyState } from '@hooks/use-card-property-state';
import { useCallback, useContext, useState } from 'react';

import { CardItemEditor } from '../../CardItemEditor';
import { CardProperty } from '../CardProperty';
import { useCardPropertyMutations } from '../hooks/card-property-mutations';
import type { CardAttributeType, ValueProvider } from '.cache/__types__';

type SingleInputCardPropertyProps = {
  attribute: CardAttributeType;
  /* The fragment that this property is editing */
  property?: CardPropertyDetailsFragment;
  cardId: string;
  validator?: () => boolean;

  positionDeps?: React.DependencyList;
  /** This is the local state for the value */
  className: string;
  inputLabel: string;
  /** The Template Type that will be used for the Property by default on creation */
  defaultTemplateValueProvider?: ValueProvider;
};

export const SingleInputCardProperty: React.FC<SingleInputCardPropertyProps> = ({
  attribute,
  property,
  cardId,
  validator,
  positionDeps = [],
  className,
  inputLabel,
  defaultTemplateValueProvider,
}) => {
  const { activeAttributeId, activeAttribute, flavour } = useContext(cardEditContext);
  const [tempValueProviderForTour, setTempValueProviderForTour] = useState<ValueProvider | null>(null);
  const propertyState = useCardPropertyState<string>(
    property,
    attribute.id,
    activeAttributeId,
    defaultTemplateValueProvider,
  );

  const { create, update, remove } = useCardPropertyMutations(cardId);

  const onSave = useCallback(
    (newValueProvider?: ValueProvider) => {
      const stage = propertyState.value ? 'done' : 'todo';

      const sharedMutationValues: Omit<CardPropertyDetailsFragment, 'id' | 'attribute_type_id'> & {
        card_id: string;
      } = {
        card_id: cardId,
        instruction_text: propertyState.instruction ?? null,
        value_provider: propertyState.isModified // Prevent updating the value provider if the user hasn't made any changes
          ? (newValueProvider ?? propertyState.valueProvider)
          : propertyState.valueProvider,
        value_locked: propertyState.valueLocked,
        is_optional: propertyState.isOptional,
        value: propertyState.value,
        template_id: property?.template_id ?? null,
        stage,
      };

      if (!property) {
        if (!activeAttribute) {
          return;
        }

        create(
          {
            attribute_type_id: activeAttribute?.id,
            ...sharedMutationValues,
          },
          activeAttribute,
        );

        return;
      }

      update(
        {
          id: property.id,
          ...sharedMutationValues,
        },
        property.attribute_type as CardAttributeType,
      );
    },
    [property, update, propertyState, cardId, activeAttribute, create],
  );

  const canDelete = canDeleteCardItem({
    flavour,
    isOptional: Boolean(property?.is_optional),
    isTemplated: Boolean(property?.template_id),
    valueProvider: property?.value_provider,
  });

  const onDelete = useCallback(() => {
    if (!property || !canDelete) {
      return;
    }

    remove(cardId, property.id, property?.attribute_type?.name ?? undefined);
  }, [cardId, property, remove, canDelete]);

  if (!propertyState.showProperty) {
    return null;
  }

  return (
    <>
      <CardProperty
        icon={null}
        trackingAttributeName={property?.attribute_type?.name || 'unknown'}
        updateTempValueProviderForTour={setTempValueProviderForTour}
        canDelete={canDelete}
        hasUnsavedChanges={propertyState.isModified}
        attributeId={attribute.id}
        isLocked={propertyState.fullyLocked}
        isLockedTooltipMessage={propertyState.lockedTooltipMessage}
        isFieldLinkedToProfile={propertyState.valueProvider === 'profile'}
        isOptional={propertyState.isOptional}
        unlinkCardField={onSave}
        editUI={
          <CardItemEditor {...propertyState} tempValueProviderForTour={tempValueProviderForTour}>
            <PropertyFormField
              value={propertyState.value}
              label={inputLabel}
              onChange={propertyState.setValue}
              locked={propertyState.valueLocked || false}
              toggleLocked={
                propertyState.valueLockEnabled
                  ? (locked) => propertyState.setValueLocked?.(locked)
                  : undefined
              }
            />
          </CardItemEditor>
        }
        onCancel={propertyState.onCancel}
        onDelete={onDelete}
        onSave={onSave}
        positionDeps={positionDeps}
        validator={validator}
        attributeDisplayName={attribute.display_name || ''}
        attributeValue={property?.value ?? ''}
      >
        <WithPlaceholder placeholderText={inputLabel} show={propertyState?.value ? false : true}>
          {(props) => (
            <div className={className} {...props} data-id={attribute.id}>
              {propertyState.showPropertyPlaceholder ? '' : propertyState.value}
            </div>
          )}
        </WithPlaceholder>
      </CardProperty>
    </>
  );
};
